import React from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { LucideIcon, LucideProps } from 'lucide-react';

type IconComponentType =
  | LucideIcon
  | React.ForwardRefExoticComponent<Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>>;

/**
 * Props for the InputIconCds component.
 *
 * @interface InputIconCdsProps
 * @extends {React.InputHTMLAttributes<HTMLInputElement>}
 *
 * @property {LucideIcon} [prefixIcon] - Optional icon to be displayed at the beginning of the input field.
 * @property {LucideIcon} [suffixIcon] - Optional icon to be displayed at the end of the input field.
 * @property {string} [iconClassName] - Optional class name to apply to the icons.
 */
interface InputIconCdsProps extends React.InputHTMLAttributes<HTMLInputElement> {
  prefixIcon?: IconComponentType;
  suffixIcon?: IconComponentType;
  iconClassName?: string;
}

/**
 * `InputIconCds` is a React functional component that renders an input field with optional prefix and suffix icons.
 * It uses `React.forwardRef` to forward the ref to the underlying input element.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} [props.className] - Additional class names to apply to the input element.
 * @param {React.ElementType} [props.prefixIcon] - An optional icon component to render as a prefix inside the input.
 * @param {React.ElementType} [props.suffixIcon] - An optional icon component to render as a suffix inside the input.
 * @param {string} [props.iconClassName] - Additional class names to apply to the icon components.
 * @param {React.Ref<HTMLInputElement>} ref - The ref to be forwarded to the input element.
 *
 * @returns {JSX.Element} The rendered input field with optional prefix and suffix icons.
 */
const InputIconCds = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<'input'> & InputIconCdsProps
>(({ className, prefixIcon: PrefixIcon, suffixIcon: SuffixIcon, iconClassName, ...props }, ref) => {
  return (
    <div className="relative w-full">
      {PrefixIcon && (
        <PrefixIcon
          className={cn(
            'absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground',
            iconClassName
          )}
        />
      )}
      <Input
        className={cn(PrefixIcon && 'pl-10', SuffixIcon && 'pr-10', className)}
        ref={ref}
        {...props}
      />
      {SuffixIcon && (
        <SuffixIcon
          className={cn(
            'absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground',
            iconClassName
          )}
        />
      )}
    </div>
  );
});
InputIconCds.displayName = 'InputIconCds';

export { InputIconCds };
export type { InputIconCdsProps };
